export interface ContactPersonInfo {
    id: string;
    firstName: string;
    lastName: string;
    name: string;
    email?: string | null;
    phone?: string | null;
    // sourceKind != OWN: name/email/phone resolve live through an OTTO user or a
    // service-provider contact — the edit form shows them read-only (the update
    // ignores them server-side) and only the role stays editable
    isLinked?: boolean;
    sourceKind?: string | null;
    // single free-text role, localized on read when it matches a proposal;
    // null for contacts that never got one
    role?: string | null;
}

export interface ContactAssignment {
    id: string;
    // null/absent = assigned directly to the node being viewed; otherwise the
    // ancestor node the assignment actually sits on
    inheritedFrom?: {
        id: string;
        title?: string | null;
        pathNodes?: ({ id: string } | null)[] | null;
    } | null;
    contactPerson: ContactPersonInfo;
}

/**
 * Splits a node's contact assignments into directly assigned and inherited ones.
 * The backend already collapses a person assigned both directly and on an
 * ancestor into a single direct assignment, so `inheritedFrom` is trusted as-is.
 */
export const partitionAssignments = (
    assignments: (ContactAssignment | null | undefined)[] | null | undefined
): { direct: ContactAssignment[]; inherited: ContactAssignment[] } => {
    const direct: ContactAssignment[] = [];
    const inherited: ContactAssignment[] = [];

    (assignments ?? []).forEach((assignment) => {
        if (!assignment) return;
        if (assignment.inheritedFrom) {
            inherited.push(assignment);
        } else {
            direct.push(assignment);
        }
    });

    return { direct, inherited };
};

/**
 * The few contacts a summary card shows: directly assigned before inherited
 * (each group in the backend's name order), capped at `max`. `hiddenCount` is
 * what the card summarises as "+ N more"; `total` is the backend's count when
 * the list was paginated, so contacts beyond the loaded page are counted too.
 */
export const pickSummaryContacts = (
    assignments: (ContactAssignment | null | undefined)[] | null | undefined,
    max: number,
    total?: number | null,
): { visible: ContactAssignment[]; hiddenCount: number } => {
    const { direct, inherited } = partitionAssignments(assignments);
    const ordered = [...direct, ...inherited];
    const visible = ordered.slice(0, max);
    const overall = Math.max(total ?? 0, ordered.length);
    return { visible, hiddenCount: overall - visible.length };
};
